import type { StateCreator } from 'zustand';
import { loadStored, saveStored } from '../hooks/usePersist';
import type { AutomationRule, AutomationActionLog } from '../services/automationEngine';
import { getDefaultRules } from '../services/automationEngine';

const PERSIST_KEY_RULES = 'automation_rules';
const PERSIST_KEY_LOGS = 'automation_logs';
const MAX_LOGS = 200;

// One-time migration: bump co2-high threshold from old default 1200 → 1000
function migrateRules(rules: AutomationRule[]): AutomationRule[] {
  return rules.map(r =>
    r.id === 'auto-co2-high' && r.threshold === 1200
      ? { ...r, threshold: 1000 }
      : r
  );
}

export interface AutomationSlice {
  automationRules: AutomationRule[];
  automationLogs: AutomationActionLog[];
  automationEnabled: boolean;

  setAutomationRules: (v: AutomationRule[] | ((p: AutomationRule[]) => AutomationRule[])) => void;
  setAutomationLogs: (v: AutomationActionLog[] | ((p: AutomationActionLog[]) => AutomationActionLog[])) => void;
  setAutomationEnabled: (v: boolean) => void;
  addAutomationRule: (rule: AutomationRule) => void;
  updateAutomationRule: (id: string, updates: Partial<AutomationRule>) => void;
  removeAutomationRule: (id: string) => void;
  addAutomationLog: (log: AutomationActionLog) => void;
  clearAutomationLogs: () => void;
}

export const createAutomationSlice: StateCreator<AutomationSlice, [], [], AutomationSlice> = (set, get) => ({
  automationRules: migrateRules(loadStored(PERSIST_KEY_RULES, getDefaultRules())),
  automationLogs: loadStored(PERSIST_KEY_LOGS, []),
  automationEnabled: loadStored('automation_enabled', true),

  setAutomationRules: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.automationRules) : v;
    saveStored(PERSIST_KEY_RULES, next);
    return { automationRules: next };
  }),

  setAutomationLogs: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.automationLogs) : v;
    saveStored(PERSIST_KEY_LOGS, next);
    return { automationLogs: next };
  }),

  setAutomationEnabled: (v) => {
    saveStored('automation_enabled', v);
    set({ automationEnabled: v });
  },

  addAutomationRule: (rule) => get().setAutomationRules(prev => [...prev, rule]),

  updateAutomationRule: (id, updates) => get().setAutomationRules(prev =>
    prev.map(r => r.id === id ? { ...r, ...updates } : r)
  ),

  removeAutomationRule: (id) => get().setAutomationRules(prev => prev.filter(r => r.id !== id)),

  addAutomationLog: (log) => get().setAutomationLogs(prev =>
    [log, ...prev].slice(0, MAX_LOGS)
  ),

  clearAutomationLogs: () => get().setAutomationLogs([]),
});
